import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GroupType, ParticipantGroup, SubmitGroupBookingRequest, GroupBookingResult } from '../types/groupBooking';
import { submitGroupBooking } from '../services/groupBookingService';
import { PlusIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { formatGroupDate } from '../utils/dateUtils';

interface GroupBookingFormProps {
  programId: string;
  programName: string;
  selectedDates: string[];
  onSubmitSuccess: (result: GroupBookingResult) => void;
}

const CAPACITY_LIMITS: Record<GroupType, number> = {
  preschool: 20,
  elementary: 40,
};

const PRESCHOOL_OPTIONS = ['유치원 6세', '유치원 7세'];
const ELEMENTARY_GRADES = ['1학년', '2학년', '3학년', '4학년', '5학년', '6학년'];

const GroupBookingForm: React.FC<GroupBookingFormProps> = ({
  programId,
  programName,
  selectedDates,
  onSubmitSuccess,
}) => {
  const { user, isAuthenticated } = useAuth();

  const [institutionName, setInstitutionName] = useState('');
  const [institutionPhone, setInstitutionPhone] = useState('');
  const [bookerName, setBookerName] = useState('');
  const [bookerPhone, setBookerPhone] = useState('');
  const [groupType, setGroupType] = useState<GroupType | ''>('');
  const [participants, setParticipants] = useState<ParticipantGroup[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 로그인 시 자동채움
  useEffect(() => {
    if (isAuthenticated && user) {
      setBookerName(user.name || '');
      setBookerPhone(user.phone || '');
    }
  }, [isAuthenticated, user]);

  // 유형 변경 시 참가자 초기화
  useEffect(() => {
    if (groupType === 'preschool') {
      setParticipants(PRESCHOOL_OPTIONS.map(label => ({ label, count: 0 })));
    } else if (groupType === 'elementary') {
      setParticipants([{ label: '초등 1학년', count: 0 }]);
    } else {
      setParticipants([]);
    }
  }, [groupType]);

  // 양식 작성 중 이탈 경고
  useEffect(() => {
    const hasInput = institutionName || bookerName || groupType;
    if (!hasInput) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [institutionName, bookerName, groupType]);

  const totalCount = participants.reduce((sum, p) => sum + (p.count || 0), 0);
  const maxCapacity = groupType ? CAPACITY_LIMITS[groupType] : 0;
  const isOverCapacity = totalCount > maxCapacity;

  const updateParticipantCount = (index: number, count: number) => {
    setParticipants(prev => prev.map((p, i) => i === index ? { ...p, count: Math.max(0, count) } : p));
  };

  const addElementaryRow = () => {
    if (groupType !== 'elementary') return;
    // 사용되지 않은 학년 찾기
    const usedGrades = participants.map(p => p.label);
    const nextGrade = ELEMENTARY_GRADES.find(g => !usedGrades.includes(`초등 ${g}`));
    if (nextGrade) {
      setParticipants(prev => [...prev, { label: `초등 ${nextGrade}`, count: 0 }]);
    }
  };

  const removeParticipantRow = (index: number) => {
    if (groupType === 'preschool') return; // 유치원은 고정 2행
    setParticipants(prev => prev.filter((_, i) => i !== index));
  };

  const updateElementaryGrade = (index: number, grade: string) => {
    setParticipants(prev => prev.map((p, i) => i === index ? { ...p, label: `초등 ${grade}` } : p));
  };

  const formatDate = formatGroupDate;

  const handleSubmit = async () => {
    setError('');

    // 검증
    if (!institutionName.trim()) { setError('예약단체명을 입력해주세요.'); return; }
    if (!institutionPhone.trim()) { setError('기관 전화번호를 입력해주세요.'); return; }
    if (!bookerName.trim()) { setError('예약자명을 입력해주세요.'); return; }
    if (!bookerPhone.trim()) { setError('예약자 휴대폰번호를 입력해주세요.'); return; }
    // 전화번호 형식 검증
    const phoneDigits = bookerPhone.replace(/[-\s]/g, '');
    if (!/^01[0-9]{8,9}$/.test(phoneDigits)) {
      setError('휴대폰번호 형식이 올바르지 않습니다. (예: 010-1234-5678)'); return;
    }
    const instPhoneDigits = institutionPhone.replace(/[-\s]/g, '');
    if (!/^0[0-9]{8,10}$/.test(instPhoneDigits)) {
      setError('기관 전화번호 형식이 올바르지 않습니다. (예: 031-866-5670)'); return;
    }
    if (!groupType) { setError('유치원 또는 초등을 선택해주세요.'); return; }
    if (totalCount === 0) { setError('참가 인원을 입력해주세요.'); return; }
    if (isOverCapacity) { setError(`${groupType === 'preschool' ? '유치원' : '초등'}은(는) 최대 ${maxCapacity}명까지 가능합니다.`); return; }

    setSubmitting(true);

    const request: SubmitGroupBookingRequest = {
      program_id: programId,
      booking_dates: selectedDates,
      institution_name: institutionName.trim(),
      institution_phone: institutionPhone.trim(),
      booker_name: bookerName.trim(),
      booker_phone: bookerPhone.trim(),
      participants: {
        type: groupType,
        groups: participants.filter(p => p.count > 0),
        total_count: totalCount,
      },
    };

    const result = await submitGroupBooking(request);

    setSubmitting(false);

    if (result.success && result.data) {
      onSubmitSuccess(result.data);
    } else {
      setError(result.error?.message || '신청에 실패했습니다.');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
      <h3 className="text-xl font-bold text-gray-800 mb-6">단체체험 신청</h3>

      {/* 선택된 날짜 */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">예약일자</label>
        <div className="flex flex-wrap gap-2">
          {selectedDates.sort().map(date => (
            <span key={date} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200">
              {formatDate(date)}
            </span>
          ))}
        </div>
      </div>

      {/* 기관 정보 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            예약단체명 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={institutionName}
            onChange={e => setInstitutionName(e.target.value)}
            placeholder="예) 동두천어린이집"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            기관전화 <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={institutionPhone}
            onChange={e => setInstitutionPhone(e.target.value)}
            placeholder="031-XXX-XXXX"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            예약자명 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={bookerName}
            onChange={e => setBookerName(e.target.value)}
            placeholder="담당자 이름"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            예약자 휴대폰 <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={bookerPhone}
            onChange={e => setBookerPhone(e.target.value)}
            placeholder="010-XXXX-XXXX"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
      </div>

      {/* 유형 선택 */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          유형 선택 <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setGroupType('preschool')}
            className={`flex-1 py-3 rounded-lg font-semibold text-sm transition-all ${
              groupType === 'preschool'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            유치원 (최대 20명)
          </button>
          <button
            type="button"
            onClick={() => setGroupType('elementary')}
            className={`flex-1 py-3 rounded-lg font-semibold text-sm transition-all ${
              groupType === 'elementary'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            초등 (최대 40명)
          </button>
        </div>
      </div>

      {/* 참가 인원 */}
      {groupType && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            참가 인원 <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            {participants.map((p, index) => (
              <div key={index} className="flex items-center gap-3">
                {groupType === 'elementary' ? (
                  <select
                    value={p.label.replace('초등 ', '')}
                    onChange={e => updateElementaryGrade(index, e.target.value)}
                    className="w-32 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {ELEMENTARY_GRADES.map(g => (
                      <option key={g} value={g}>{`초등 ${g}`}</option>
                    ))}
                  </select>
                ) : (
                  <span className="w-32 text-sm text-gray-700 font-medium">{p.label}</span>
                )}
                <input
                  type="number"
                  min={0}
                  value={p.count || ''}
                  onChange={e => updateParticipantCount(index, parseInt(e.target.value) || 0)}
                  placeholder="0"
                  className="w-24 px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <span className="text-sm text-gray-500">명</span>
                {groupType === 'elementary' && participants.length > 1 && (
                  <button onClick={() => removeParticipantRow(index)} className="p-1 text-red-400 hover:text-red-600">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}

            {/* 초등: 행 추가 */}
            {groupType === 'elementary' && participants.length < 6 && (
              <button
                onClick={addElementaryRow}
                className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 font-medium"
              >
                <PlusIcon className="w-4 h-4" /> 학년 추가
              </button>
            )}
          </div>

          {/* 합계 */}
          <div className={`mt-3 flex items-center gap-2 text-sm font-semibold ${isOverCapacity ? 'text-red-600' : 'text-gray-700'}`}>
            합계: {totalCount}명 / 최대 {maxCapacity}명
            {isOverCapacity && (
              <span className="flex items-center gap-1 text-red-500">
                <ExclamationTriangleIcon className="w-4 h-4" /> 인원 초과
              </span>
            )}
          </div>
        </div>
      )}

      {/* 안내 문구 */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-amber-800 font-medium mb-1">
          ※ 홈페이지 뿐 아니라 전화로도 접수 가능합니다. (031-866-5670)
        </p>
        <p className="text-sm text-amber-800">
          ※ 홈페이지 신청 후에도 담당자와의 확인통화를 거쳐야만 접수가 확정되니 유의하세요.
        </p>
      </div>

      {/* 에러 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 제출 */}
      <button
        onClick={handleSubmit}
        disabled={submitting || isOverCapacity}
        className={`w-full py-3.5 rounded-lg font-bold text-base transition-all ${
          submitting || isOverCapacity
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
        }`}
      >
        {submitting ? '신청 중...' : '단체체험 신청하기'}
      </button>
    </div>
  );
};

export default GroupBookingForm;
